import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ToastTone = 'info' | 'success' | 'warning' | 'danger';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastInput {
  tone?: ToastTone;
  title?: string;
  message: string;
  /** Durée en ms avant dismiss auto. 0 = persistant (croix uniquement). Défaut : 5000, sauf `danger` (0). */
  duration?: number;
  /** Bouton d'action optionnel (ex: « Voir mes tâches » pour ouvrir un deep-link). */
  action?: ToastAction;
}

interface Toast extends Required<Omit<ToastInput, 'title' | 'action'>> {
  id: string;
  title?: string;
  action?: ToastAction;
}

interface ToastContextValue {
  toast: (t: ToastInput) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const handle = timers.current.get(id);
    if (handle) {
      clearTimeout(handle);
      timers.current.delete(id);
    }
  }, []);

  const toast = useCallback(
    (input: ToastInput) => {
      const tone: ToastTone = input.tone ?? 'info';
      const id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`;
      const duration = input.duration ?? (tone === 'danger' ? 0 : 5000);
      const entry: Toast = {
        id,
        tone,
        message: input.message,
        duration,
        title: input.title,
        action: input.action,
      };
      setToasts((prev) => [...prev, entry]);
      if (duration > 0) {
        const handle = setTimeout(() => dismiss(id), duration);
        timers.current.set(id, handle);
      }
    },
    [dismiss],
  );

  useEffect(() => {
    const map = timers.current;
    return () => {
      map.forEach(clearTimeout);
      map.clear();
    };
  }, []);

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="pointer-events-none fixed right-4 top-4 z-[60] flex w-96 max-w-[calc(100vw-2rem)] flex-col gap-2"
      >
        {toasts.map((t) => (
          <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): (t: ToastInput) => void {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be called inside <ToastProvider>');
  return ctx.toast;
}

// ---------------------------------------------------------------------------

const TONE_STYLES: Record<
  ToastTone,
  { container: string; icon: React.ComponentType<{ className?: string }> }
> = {
  info: { container: 'border-accent-100 bg-white', icon: Info },
  success: { container: 'border-success-100 bg-white', icon: CheckCircle2 },
  warning: { container: 'border-warning-100 bg-white', icon: AlertTriangle },
  danger: { container: 'border-danger-100 bg-white', icon: AlertCircle },
};

const TONE_ICON_COLOR: Record<ToastTone, string> = {
  info: 'text-accent',
  success: 'text-success-600',
  warning: 'text-warning-600',
  danger: 'text-danger-600',
};

function ToastCard({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const { container, icon: Icon } = TONE_STYLES[toast.tone];
  return (
    <div
      role={toast.tone === 'danger' ? 'alert' : 'status'}
      className={cn(
        'pointer-events-auto flex gap-3 rounded-lg border p-3 shadow-popover animate-slide-in-right',
        container,
      )}
    >
      <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', TONE_ICON_COLOR[toast.tone])} />
      <div className="min-w-0 flex-1">
        {toast.title && <div className="text-sm font-semibold text-brand">{toast.title}</div>}
        <div className={cn('text-sm text-text-primary', toast.title && 'mt-0.5 text-text-muted')}>
          {toast.message}
        </div>
        {toast.action && (
          <button
            type="button"
            onClick={() => {
              toast.action!.onClick();
              onDismiss();
            }}
            className="mt-2 text-xs font-medium text-accent hover:text-accent-hover underline-offset-2 hover:underline"
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="shrink-0 rounded-md p-0.5 text-text-muted hover:bg-gray-100 hover:text-brand"
        aria-label="Fermer"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
